/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "@i18n";
import { fileViewModes } from "./constants";
import useFileManagerShared from "./useFileManagerShared";
import type { SlickgridReactInstance } from "slickgrid-react";
import type { SlickEventData } from "@slickgrid-universal/common";
import type { ContextMenuItem, FileManagerHandle } from "./FileManagerComponent";
import type {
  FileManagerData,
  FileManagerRequestArgs,
  FileManagerRow,
  FileManagerStateUpdate,
} from "./fileSystemServer.schema";

/**
 * A context-menu entry as this hook reads and writes it: the shared item plus the `extensions`
 * allow-list the file manager tags options with and the per-selection `disabled` flag set below.
 */
type FileContextMenuItem = ContextMenuItem & { disabled?: boolean; extensions?: string[] };

/** The context-menu configuration `FileManagerComponent` passes down. */
interface FileContextMenuSettings {
  file: FileContextMenuItem[];
  folder: FileContextMenuItem[];
  layout: FileContextMenuItem[];
  visible: boolean;
}

/** State of the MUI context menu this hook drives; `null` position/options mean "closed". */
interface FileMenuState {
  open: boolean;
  source: string;
  position: { top: number; left: number } | null;
  data?: FileManagerRow | null;
  options: FileContextMenuItem[] | null;
}

/**
 * The two coordinate fields the context menu positions itself with. Both React's synthetic mouse
 * event and slickgrid's `SlickEventData` carry them (slickgrid declares them optional).
 */
type ContextMenuEvent = { pageX?: number; pageY?: number };

/** The files grid's imperative handle as the caller holds it. */
type FilesGridRef = React.RefObject<FileManagerHandle | null>;

/** Method shorthand on the callbacks — see the note in `useFileManagerShared`. */
interface UseFileManagerFilesProps {
  setSelectedFolder(folder: FileManagerRow): void;
  onFileClick?(clickedRow: FileManagerRow): void;
  onFileSelect?(files: FileManagerRow[]): void;
  fileManagerData?: FileManagerData;
  contextMenuSettings?: FileContextMenuSettings;
  /**
   * Returns the ids of the context-menu entries to disable. The parameter is optional because the
   * layout (empty-space) menu calls this with no selection at all.
   */
  menuOpen?(selectedFiles?: FileManagerRow[]): string[];
  /** Built against the caller's own row type; only forwarded to the grid. */
  fileColumns?: object[];
  tempFolder?: FileManagerRow;
  /** `null` is the "no active search" sentinel the debounce state starts in. */
  handleSearchFiles?(query: string | null): void;
  onFileLoad?(files: FileManagerRow[]): void;
  beforeSending?(args: FileManagerRequestArgs): void;
  allowDragAndDropUpload?: boolean;
  selectedFolder?: FileManagerRow;
  filesViewMode?: string;
  setSelectedFilesInIconsMode?(update: FileManagerStateUpdate<FileManagerRow[]>): void;
  selectedFilesInIconsMode?: FileManagerRow[];
  setIsViewRendered?(rendered: boolean): void;
  isViewRendered?: boolean;
  disableDragAndDropUpload?: boolean;
  preSearchText?: string;
  /**
   * Active external-link counts keyed by resource id. Part of the column re-seed key: the name-cell
   * badge lives in a formatter closure, and SlickGrid keeps mount-time closures until the columns
   * are re-seeded — without this the badge never appears (the counts land after the grid mounts).
   */
  externalLinkCounts?: Record<string, number>;
}

function useFileManagerFiles({
  setSelectedFolder,
  onFileClick = () => {},
  onFileSelect = () => {},
  fileManagerData = {},
  contextMenuSettings,
  menuOpen = () => [],
  fileColumns = [],
  tempFolder = {},
  handleSearchFiles = () => {},
  onFileLoad = () => {},
  beforeSending = () => {},
  allowDragAndDropUpload = false,
  selectedFolder = {},
  filesViewMode = "DETAILED_FILES",
  setSelectedFilesInIconsMode = () => {},
  selectedFilesInIconsMode = [],
  setIsViewRendered = () => {},
  isViewRendered: _isViewRendered = false,
  disableDragAndDropUpload = false,
  preSearchText = "",
  externalLinkCounts = {},
}: UseFileManagerFilesProps) {
  const { t } = useTranslation();

  // Use shared hook for platform-agnostic logic (breadcrumbs, search, file filtering)
  const {
    query,
    setQuery,
    debounced,
    setDebounced,
    showPathColumn,
    setShowPathColumn,
    filesData,
    setFilesData,
    breadCrumbData,
    setBreadCrumbData,
    isFirstRender,
    setIsFirstRender,
  } = useFileManagerShared({
    setSelectedFolder,
    fileManagerData,
    tempFolder,
    handleSearchFiles,
    selectedFolder,
    preSearchText,
    onFileLoad,
  });

  // Web-specific state
  const [disabledRows, setDisabledRows] = useState<string[]>([]);
  const [columnDefinitions, setColumnDefinitions] = useState<object[]>(fileColumns);
  const [menuState, setMenuState] = useState<FileMenuState>({
    open: false,
    source: "",
    position: null,
    data: null,
    options: null,
  });
  const [isOver, setIsOver] = useState(false);

  // Refs
  const contextMenuSettingsRef = useRef<FileContextMenuSettings | null>(null);
  const isMounted = useRef(false);
  const dragCounter = useRef(0);
  // `searchRef`/`tempRef` are returned below but nothing destructures them; typed at their
  // (unused) intent rather than left as `any`.
  const searchRef = useRef<HTMLInputElement | null>(null);
  const tempRef = useRef<Record<string, unknown>>({});

  // Handlers
  const setSelectedRows = (indexes: (string | number)[], dataGrid: FilesGridRef) => {
    if (Array.isArray(indexes)) {
      // Rows are addressed by numeric index everywhere below; the handle's public signature
      // (`FileManagerHandle.setSelectedRows`) is the wider `(string | number)[]`, so drop anything
      // that is not usable as a row index instead of asserting the array's element type away.
      const rowIndexes = indexes.filter((index): index is number => typeof index === "number");
      if (filesViewMode === fileViewModes.DETAILED_VIEW) {
        dataGrid.current?.slickGrid?.setSelectedRows(rowIndexes);
      } else if (filesViewMode === fileViewModes.LARGE_ICONS_VIEW) {
        if (indexes.length == 0) {
          setSelectedFilesInIconsMode([]);
        } else {
          const filesToSelect: FileManagerRow[] = [];
          rowIndexes.forEach((index: number) => {
            if (filesData[index]) {
              // Guarded on the line above; `noUncheckedIndexedAccess` does not narrow a variable index.
              filesToSelect.push(filesData[index]!);
            }
          });
          setSelectedFilesInIconsMode(filesToSelect);
        }
      }
    }
  };

  const recordClick = (clickedRow: FileManagerRow) => {
    if (clickedRow.resourceType === "RESOURCE_TYPE_FOLDER" || clickedRow.resourceType === "RESOURCE_TYPE_TRANSMITTAL") {
      setSelectedFolder(clickedRow);
    }
    onFileClick(clickedRow);
  };

  const selectionHandlerSlickGrid = (dataGrid: FilesGridRef) => {
    const selectedData = dataGrid.current?.slickGrid
      ?.getSelectedRows()
      // Reached only through the `?.` chain above, so both are non-null here.
      .map((data: number) => dataGrid.current!.slickGrid!.getDataItem(data));
    const filteredData = selectedData?.filter(Boolean) || [];
    onFileSelect(filteredData);
  };

  const searchFileHandler = (args: React.ChangeEvent<HTMLInputElement> | string) => {
    const value = (typeof args === "string" ? args : args.target.value).toLowerCase() || "";
    setQuery(value);
  };

  const handleClose = () => {
    setMenuState((s: FileMenuState) => ({ ...s, source: "", open: false, position: null }));
  };

  const handleContextMenuForResource = (e: ContextMenuEvent, file: FileManagerRow, selectedFiles: FileManagerRow[]) => {
    // `resourceType` is optional on the row, and a row without one deliberately misses this test.
    let options = (["RESOURCE_TYPE_FOLDER", "RESOURCE_TYPE_TRANSMITTAL"] as (string | undefined)[]).includes(
      file.resourceType,
    )
      ? contextMenuSettingsRef.current?.folder
      : contextMenuSettingsRef.current?.file;

    // Options may declare an `extensions` allow-list (e.g. Export to PDF for dwg/dxf only).
    // Items without `extensions` always show; items with it show only for a matching file.
    const fileExt = (file.name?.split(".").pop() || "").toLowerCase();
    options = options?.filter((opt: FileContextMenuItem) => !opt.extensions || opt.extensions.includes(fileExt));

    const disabledItems = menuOpen(selectedFiles);
    options = options?.map((opt: FileContextMenuItem) => {
      opt.disabled = false;
      if (disabledItems.includes(opt.id)) {
        opt.disabled = true;
      }
      return opt;
    });
    setMenuState({
      // `options` and `visible` are both absent until the settings object arrives; `null`/`false`
      // are this state's own "closed" sentinels, so normalise onto them rather than assert.
      options: options ?? null,
      source: file.resourceType === "RESOURCE_TYPE_FOLDER" ? "FOLDER" : "FILE",
      open: contextMenuSettingsRef.current?.visible ?? false,
      position: {
        // `SlickEventData` declares these optional because it can wrap no native event at all;
        // a real contextmenu event always carries them, and 0 is still a valid anchor.
        top: e.pageY ?? 0,
        left: e.pageX ?? 0,
      },
      data: file,
    });
  };

  const contextMenuForIconsView = (e: React.MouseEvent, file: FileManagerRow) => {
    e.stopPropagation();
    e.preventDefault();
    const selectedFiles = [...selectedFilesInIconsMode];
    setSelectedFilesInIconsMode((prev: FileManagerRow[]) => [...prev, file]);
    handleContextMenuForResource(e, file, selectedFiles);
  };

  const handleGridCreated = (reactGrid: SlickgridReactInstance, _dataGrid: FilesGridRef) => {
    setIsViewRendered(true);
    reactGrid.slickGrid.onContextMenu.subscribe((e: SlickEventData) => {
      e.preventDefault();
      const cell = reactGrid.slickGrid.getCellFromEvent(e);
      if (!cell) return;

      const row = cell.row;
      const dataView = reactGrid.slickGrid.getData();
      const item = dataView.getItem(row);

      if (!reactGrid.slickGrid.getSelectedRows().includes(row)) {
        reactGrid.slickGrid.setSelectedRows([row]);
      }
      const selectedFiles = reactGrid.slickGrid
        .getSelectedRows()
        .map((data: number) => reactGrid.slickGrid.getDataItem(data));
      handleContextMenuForResource(e, item, selectedFiles);
    });
    reactGrid.slickGrid.onDblClick.subscribe((e: SlickEventData) => {
      e.preventDefault();
      const cell = reactGrid.slickGrid.getCellFromEvent(e);
      if (!cell) return;

      const row = cell.row;
      const dataView = reactGrid.slickGrid.getData();
      const item = dataView.getItem(row);
      recordClick(item);
    });
    reactGrid.slickGrid.onScroll.subscribe(() => {
      setMenuState((s: FileMenuState) => (s.open ? { ...s, source: "", open: false, position: null } : s));
    });
  };

  const onContextMenu = (args: React.MouseEvent) => {
    // `target` is only an `EventTarget`; a grid row can only ever be reached from an element.
    const target = args.target instanceof Element ? args.target : null;
    if (!target?.closest(".slick-row")) {
      if (menuState.position) {
        return;
      }
      args.preventDefault();
      let options = contextMenuSettings?.layout || [];
      const disabledItems = menuOpen();
      options = options.map((opt: FileContextMenuItem) => {
        opt.disabled = false;
        if (disabledItems.includes(opt.id)) {
          opt.disabled = true;
        }
        return opt;
      });
      setMenuState({
        options,
        source: "LAYOUT",
        // Undefined until the settings object arrives; `false` is this state's "closed" value.
        open: contextMenuSettings?.visible ?? false,
        position: {
          top: args.pageY,
          left: args.pageX,
        },
      });
    }
  };

  // React declares `dataTransfer` non-nullable on its synthetic event, but the DOM declares
  // `DragEvent.dataTransfer` as `DataTransfer | null`; read the native event so the guard is typed.
  const isFileDrag = (e: React.DragEvent) => Array.from(e.nativeEvent.dataTransfer?.types || []).includes("Files");

  const onDragEnter = (e: React.DragEvent<HTMLElement>) => {
    if (!isFileDrag(e) || !allowDragAndDropUpload) return;
    e.preventDefault();
    dragCounter.current++;
    setIsOver(true);
  };

  const onDragLeave = (e: React.DragEvent<HTMLElement>) => {
    if (!isFileDrag(e) || !allowDragAndDropUpload) return;
    dragCounter.current = Math.max(0, dragCounter.current - 1);
    if (dragCounter.current === 0) {
      setIsOver(false);
    }
  };

  const onDragOver = (e: React.DragEvent<HTMLElement>) => {
    if (!isFileDrag(e) || !allowDragAndDropUpload) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleDrop = (e: React.DragEvent<HTMLElement>) => {
    if (!isFileDrag(e) || !allowDragAndDropUpload) return;
    e.preventDefault();
    dragCounter.current = 0;
    setIsOver(false);
    beforeSending({
      action: "upload",
      files: e.dataTransfer.files,
    });
  };

  const handleMenuClick = (item: FileContextMenuItem, dataGrid: FilesGridRef) => {
    let selectedData: FileManagerRow[] = [];
    if (filesViewMode == fileViewModes.DETAILED_VIEW) {
      // No grid on the ref means nothing is selected; keep the `[]` above rather than assigning
      // `undefined` and throwing on the reads below.
      const grid = dataGrid.current?.slickGrid;
      selectedData = grid ? grid.getSelectedRows().map((data: number) => grid.getDataItem(data)) : [];
    } else if (filesViewMode === fileViewModes.LARGE_ICONS_VIEW) {
      selectedData = selectedFilesInIconsMode;
    }

    if (item.id == "cut") {
      // `id` is optional on a row (the synthesised breadcrumb root has none); such a row cannot be
      // matched against `disabledRows` anyway, so it is dropped rather than stored as `undefined`.
      const ids = selectedData
        .map((file: FileManagerRow) => file.id)
        .filter((id: string | undefined): id is string => id !== undefined);
      setDisabledRows(ids);
    } else if (item.id == "paste") {
      setDisabledRows([]);
    } else if (
      item.id == "open" &&
      (selectedData[0]?.resourceType === "RESOURCE_TYPE_FOLDER" ||
        selectedData[0]?.resourceType === "RESOURCE_TYPE_TRANSMITTAL")
    ) {
      setSelectedFolder(selectedData[0]);
    }
    if (item.onClick) {
      item.onClick(selectedData);
    }
    setMenuState((s: FileMenuState) => ({ ...s, source: "", open: false, position: null }));
  };

  const checkIconBoxSelect = (args: React.ChangeEvent<HTMLInputElement>, file: FileManagerRow) => {
    let files = [...selectedFilesInIconsMode];
    if (args.target.checked) {
      files.push(file);
      setSelectedFilesInIconsMode([...files]);
    } else {
      files = files.filter((item) => item.id != file.id);
      setSelectedFilesInIconsMode([...files]);
    }
  };

  // Web-specific effects (shared logic is in useFileManagerShared)
  useEffect(() => {
    // @ts-expect-error -- `contextMenuSettings` is optional on this hook's props but required by the grid it is forwarded to; the file manager renders without a context menu when it is absent.
    contextMenuSettingsRef.current = contextMenuSettings;
  }, [contextMenuSettings]);

  // Clear web search input when folder changes
  useEffect(() => {
    // `HTMLInputElement.value` is `[LegacyNullToEmptyString]`, so the previous `= null` already
    // cleared the box; `""` says so without asserting the element's type.
    const searchEle = document.getElementById("fileSearchBox");
    if (searchEle instanceof HTMLInputElement) {
      searchEle.value = "";
    }
  }, [selectedFolder.id]);

  const fileColumnsKey = fileColumns
    .map((c: object) => {
      const col = c as { id?: string; initiallyHidden?: boolean };
      return `${col.id}:${col.initiallyHidden ? 1 : 0}`;
    })
    .join("|");

  // The external-link badge reaches cells through formatter closures, which SlickGrid holds from
  // mount until the columns re-seed. Keying the re-seed on the counts' CONTENT is what refreshes
  // the closures when counts arrive or change; identity of `fileColumns` is useless for this (a new
  // array every render) and would loop the effect.
  const externalLinkCountsKey = Object.entries(externalLinkCounts)
    .map(([id, count]) => `${id}:${count}`)
    .sort()
    .join("|");

  useEffect(() => {
    setColumnDefinitions(() => [
      ...fileColumns,
      ...(showPathColumn
        ? [
            {
              id: "path",
              field: "path",
              name: "Path",
              // Slickgrid calls a formatter as (row, cell, value, columnDef, dataContext); the
              // parameter names here predate that and are kept verbatim - `value` is the ROW.
              formatter: (data: number, params: number, a: unknown, b: unknown, value: FileManagerRow) => {
                // `path` reaches the row through the index signature, so it arrives as `unknown`.
                const path = value.path;
                return Array.isArray(path) ? `/${path.join("/")}` : "/";
              },
              minWidth: 200,
            },
          ]
        : []),
    ]);
  }, [showPathColumn, fileColumnsKey, externalLinkCountsKey]);

  useEffect(() => {
    if (isMounted.current) {
      onFileSelect(selectedFilesInIconsMode);
    } else {
      isMounted.current = true;
    }
  }, [selectedFilesInIconsMode.length]);

  return {
    // State
    disabledRows,
    setDisabledRows,
    breadCrumbData,
    setBreadCrumbData,
    filesData,
    setFilesData,
    showPathColumn,
    setShowPathColumn,
    isFirstRender,
    setIsFirstRender,
    columnDefinitions,
    setColumnDefinitions,
    menuState,
    setMenuState,
    debounced,
    setDebounced,
    query,
    setQuery,
    isOver,
    setIsOver,

    // Refs
    contextMenuSettingsRef,
    isMounted,
    dragCounter,
    searchRef,
    tempRef,

    // Handlers
    setSelectedRows,
    recordClick,
    selectionHandlerSlickGrid,
    searchFileHandler,
    handleClose,
    handleContextMenuForResource,
    contextMenuForIconsView,
    handleGridCreated,
    onContextMenu,
    isFileDrag,
    onDragEnter,
    onDragLeave,
    onDragOver,
    handleDrop,
    handleMenuClick,
    checkIconBoxSelect,

    // Translation
    t,

    // Props passthrough
    selectedFolder,
    filesViewMode,
    selectedFilesInIconsMode,
    disableDragAndDropUpload,
    allowDragAndDropUpload,
  };
}

export default useFileManagerFiles;
